import React, { useState } from "react";
import { toast } from "sonner";
import { Archive } from "lucide-react";
import { api, apiError, BACKEND_URL } from "../../lib/api";

/**
 * Download-a-ZIP-of-this-group button.
 *
 * v1.40.2 — streaming download rewrite.
 * -------------------------------------
 * The old implementation `fetch()`→`res.blob()` buffered the ENTIRE zip
 * in browser RAM before writing to disk. For a group of 5–10 big
 * clips that's 500 MB–2 GB held in memory: multi-second delays before
 * the browser's Save dialog opens, and OOM crashes on iOS Safari.
 *
 * New flow (fixes the "30s+ hang + 500/503/504" complaints):
 *   1) Fast **preflight** to /staff/groups/{id}/zip/preview — validates
 *      the group and returns entry count + filename in <100ms. If the
 *      group is empty or Dropbox is down, we surface a toast IMMEDIATELY
 *      instead of waiting for the streaming endpoint to time out.
 *   2) On success, trigger a **plain-anchor navigation** to the streaming
 *      endpoint. The browser streams the ZIP directly to disk (no blob
 *      buffer, no memory ceiling). Cookies (`SameSite=None; Secure`) are
 *      sent automatically same-site so auth just works.
 */
export function GroupZipButton({ groupId, itemCount, downloadableCount }) {
  const [loading, setLoading] = useState(false);
  const nDownloadable =
    typeof downloadableCount === "number" ? downloadableCount : itemCount;
  const noneDownloadable = nDownloadable === 0;

  const handleClick = async () => {
    if (loading || noneDownloadable) return;
    setLoading(true);
    try {
      // 1) Fast preflight — surfaces 404/503 as a clean toast without
      //    making the user wait for the streaming endpoint to time out.
      const { data } = await api.get(`/staff/groups/${groupId}/zip/preview`);
      const count = data?.count ?? nDownloadable;

      // 2) Trigger direct browser streaming download. Anchor navigation
      //    (rather than fetch+blob) lets the browser write bytes straight
      //    to disk as they arrive from the server — no memory ceiling,
      //    no waiting for the whole file before "Save" appears.
      const url = `${BACKEND_URL}/api/staff/groups/${groupId}/zip`;
      const a = document.createElement("a");
      a.href = url;
      // NOTE: `download` attribute is a hint. The server also sends
      // Content-Disposition: attachment;filename=... — that filename
      // wins for cross-origin responses in most browsers.
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();

      toast.success(
        count === 1
          ? "Download avviato — 1 file"
          : `Download avviato — ${count} file`,
        {
          description:
            "Il ZIP si scarica direttamente sul disco. Puoi continuare a lavorare.",
        },
      );
    } catch (e) {
      toast.error(apiError(e) || "Impossibile scaricare il gruppo");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading || noneDownloadable}
      data-testid={`prod-group-zip-btn-${groupId}`}
      title={
        noneDownloadable
          ? "Nessun file scaricabile in questo gruppo (i video erano troppo grandi per Telegram e non sono stati ricaricati via magic link)"
          : `Scarica gli ${nDownloadable} file scaricabili di questo gruppo in un unico ZIP`
      }
      className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-medium transition-colors ${
        noneDownloadable
          ? "border border-neutral-300 bg-neutral-100 text-neutral-400 cursor-not-allowed"
          : "border border-[#0061FF] bg-white text-[#0061FF] hover:bg-blue-50 disabled:opacity-60"
      }`}
    >
      <Archive className={`h-3 w-3 ${loading ? "animate-pulse" : ""}`} />
      {noneDownloadable
        ? "ZIP non disponibile"
        : loading
        ? "Verifica…"
        : `ZIP gruppo (${nDownloadable})`}
    </button>
  );
}
